import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';

import { CompanyMapper } from '@shared-classes';
import {
  DianCompany,
  DianPurchase,
  SupportDocumentRequest,
  XMLResponse,
} from '@shared-models';

import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { PurchasesService } from 'src/app/@pages/purchases/purchases.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AppState } from 'src/app/state/app.state';
import { selectCompaniesList } from 'src/app/state/selectors/user-companies.selectors';

import { DianInvoiceService } from './dian-invoice.service';
import { DianService } from './dian.service';
import { PurchaseMapper } from '../../classes/mappers/purchase.mapper';
import { AlertService } from '../alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class DianSupportingDocumentService {
  private cookieAuthService = inject(CookieAuthService);
  private alertService = inject(AlertService);
  private store = inject(Store<AppState>);

  private spinnerService = inject(NgxSpinnerService);
  private translate = inject(TranslateService);
  private dianService = inject(DianService);
  private dianInvoiceService = inject(DianInvoiceService);
  private purchasesService = inject(PurchasesService);
  private destroyRef = inject(DestroyRef);

  companyId = '';
  companyName = '';
  companyObject = {} as IStorageCompany;

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyId = this.companyObject.Id_company!.toString();
  }

  async sendDian(invoiceId: number) {
    try {
      this.getAuthValues();
      const purchase = await this.getPurchase(invoiceId);
      await this.validateProviderDocumentType(purchase);
      const isValid = await this.validatePurchaseSended(purchase);
      if (!isValid) {
        throw new Error('The invoice could not be sent.');
      }
      const dianCompany = await this.createDianCompany(
        purchase.electronic_support.voucher!,
        this.companyId.toString(),
      );
      const confirmed = await this.askUserConfirmation();
      if (!confirmed) {
        throw new Error('User cancelled the operation.');
      }
      this.spinnerService.show();
      const dianResponse = await this.sendRequest(purchase, dianCompany);
      return dianResponse;
    } catch (error) {
      this.spinnerService.hide();
      return Promise.reject(error);
    }
  }

  async getPurchase(id: number) {
    return new Promise<DianPurchase>((resolve) => {
      this.purchasesService
        .getPurchaseInvoice(id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((item: DianPurchase) => {
          resolve(item);
        });
    });
  }

  async validatePurchaseSended(invoice: DianPurchase): Promise<boolean> {
    const cuds = invoice.electronic_support?.cuds;
    if (!cuds) {
      return true;
    }
    this.spinnerService.show();
    try {
      const response = await firstValueFrom(
        this.dianService.getStatus(cuds, this.companyId),
      );
      this.spinnerService.hide();
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('global.dian.purchases.already-sent'),
            message: `${this.translate.instant('Status')}: ${response.StatusDescription}`,
            type: 'warning',
            acceptText: 'Ok',
          })
          .subscribe({ accept: () => resolve() });
      });
      throw new Error('Purchase already sent');
    } catch (error) {
      this.spinnerService.hide();
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('global.alerts.title.info'),
            message: `CUDS: ${cuds}`,
            type: 'info',
            acceptText: 'Ok',
          })
          .subscribe({ accept: () => resolve() });
      });
      throw error;
    }
  }

  askUserConfirmation(): Promise<boolean> {
    return new Promise((resolve) => {
      this.alertService
        .open({
          title: this.translate.instant('global.alerts.title.warning'),
          message: this.translate.instant('global.dian.purchases.confirm-send'),
          type: 'warning',
          acceptText: this.translate.instant('Yes'),
          rejectText: this.translate.instant('Abort'),
        })
        .subscribe({
          accept: () => resolve(true),
          reject: () => resolve(false),
          close: () => resolve(false),
        });
    });
  }

  async createDianCompany(voucher: number, companyId: string) {
    return new Promise<DianCompany>((resolve, reject) => {
      const companyList = this.store.select(selectCompaniesList);
      companyList.subscribe((data) => {
        const company = data.find(
          (item) => item.Id_company.toString() === companyId.toString(),
        );
        const prefixRaw = company!.prefixes.find(
          (p) => p.voucher === voucher && p.document_type === 'DS',
        )!;
        const prefixHasNulls = Object.values(prefixRaw).some(
          (prefix) => prefix === null || prefix === undefined,
        );
        if (prefixHasNulls) {
          this.showErrorAlert(
            this.translate.instant('global.company.prefix.prefix-config-error'),
          );
          reject('The ds_prefix is not configured correctly.');
        } else {
          const dianCompany = CompanyMapper.mapper(prefixRaw, company!);
          resolve(dianCompany);
        }
      });
    });
  }

  async sendRequest(purchase: DianPurchase, dianCompany: DianCompany) {
    return new Promise((resolve, reject) => {
      const parsePurchase = PurchaseMapper.mapper(purchase);
      this.dianInvoiceService
        .selectCurrency()
        .then((currency) => {
          const dianRequiredObject: SupportDocumentRequest = {
            company: dianCompany,
            purchase: parsePurchase,
          };
          this.dianService
            .sendBillSyncSupportDocument(dianRequiredObject)
            .subscribe(
              async (dianResponse: XMLResponse) => {
                if (
                  dianResponse.StatusCode === '00' ||
                  (dianResponse.string &&
                    dianResponse.string.includes('Regla: 90'))
                  // Rule: 90 is when the document has already been processed previously, then proceed to save the cuds.
                ) {
                  await this.onSuccessFullRequest(
                    parsePurchase,
                    currency,
                    dianResponse,
                  );
                  resolve(dianResponse);
                } else {
                  this.alertService.open(
                    this.dianInvoiceService.buildAlert(dianResponse),
                  );
                  reject(dianResponse.StatusMessage);
                }
              },
              async (error) => {
                await this.dianInvoiceService.onUnsuccessFullRequest(error);
                reject(error);
              },
            );
        })
        .catch(async (error) => {
          await this.dianInvoiceService.onUnsuccessFullRequest(error);
          reject(error);
        });
    });
  }

  async onSuccessFullRequest(
    purchase: DianPurchase,
    currency: string,
    dianResponse: XMLResponse,
  ) {
    return new Promise<unknown>((resolve, reject) => {
      (async () => {
        try {
          this.purchasesService
            .setCudsPurchase(
              parseInt(purchase.id_invoice.toString()),
              dianResponse.XmlDocumentKey,
              dianResponse.StatusCode,
              currency,
            )
            .subscribe({
              next: (result) => {
                this.spinnerService.hide();
                this.alertService.open(
                  this.dianInvoiceService.buildAlert(dianResponse),
                );
                resolve(result);
              },
              error: (error) => {
                this.spinnerService.hide();
                this.showErrorAlert(error);
                reject(error);
              },
            });
        } catch (error) {
          this.spinnerService.hide();
          this.alertService.open({
            title: 'Error',
            message: error as string,
            type: 'error',
            showClose: true,
          });
          reject(error);
        }
      })();
    });
  }

  private async validateProviderDocumentType(
    purchase: DianPurchase,
  ): Promise<void> {
    const provider = purchase.provider;
    const isColombianProvider = provider.country?.id_country === '44';
    const hasNit = provider.types_docs?.dian_code === '31';
    if (isColombianProvider && !hasNit) {
      await new Promise<void>((resolve) => {
        this.alertService
          .open({
            title: this.translate.instant('global.dian.purchases.error'),
            message: this.translate.instant(
              'global.dian.purchases.provider-must-have-nit',
            ),
            type: 'error',
            showClose: true,
          })
          .subscribe({ accept: () => resolve(), close: () => resolve() });
      });
      throw new Error('Colombian provider must have NIT (dian_code 31).');
    }
  }

  private showErrorAlert(error: undefined | string) {
    this.alertService.open({
      title: this.translate.instant('global.dian.purchases.error'),
      message: error!,
      type: 'error',
      showClose: true,
    });
  }
}
